/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Ewald summation for the crystal MD electrostatics.
 *
 * The damped-shifted-force (DSF) Coulomb is a real-space-only
 * approximation: accurate only as far as its cutoff reaches, which the
 * minimum-image convention caps at half the box. On a modest supercell
 * that underbinds the lattice (it is why our ice melts too low) and
 * forces the kernel to stay O(N²).
 *
 * Ewald is the exact treatment. It splits 1/r into
 *   1/r = erfc(αr)/r  +  erf(αr)/r
 * and sums the first part in real space (short-ranged — a small fixed
 * cutoff) and the second in reciprocal space (smooth — a sum over the
 * reciprocal lattice). The total is the true, infinitely-periodic
 * electrostatic energy, independent of supercell size.
 *
 * Four contributions:
 *   - real space      C·Σ qᵢqⱼ erfc(αr)/r           (pairs within rc)
 *   - reciprocal      C·(2π/V)·Σ_k exp(−k²/4α²)/k²·|S(k)|²
 *   - self-energy     −C·(α/√π)·Σ qᵢ²
 *   - exclusion       −C·Σ qᵢqⱼ erf(αr)/r           (intramolecular pairs)
 * The exclusion term is needed because the reciprocal sum implicitly
 * includes every pair — including the intramolecular ones a rigid
 * molecule must NOT have explicit Coulomb between.
 *
 * Performance — the reciprocal sum is the cost, and a naive form
 * evaluates cos/sin for every (k-vector, charge) pair. Instead this
 * uses INCREMENTAL STRUCTURE FACTORS: per charge it takes three
 * cos/sin (one per reciprocal basis vector), builds the integer powers
 * exp(i·n·b·r) by a trig-free recurrence, and assembles exp(i·k·r) for
 * every k as a product of three table lookups. Trig calls drop from
 * O(N_k·N) to O(3N); the k-loop is plain multiply-add. Forces
 * accumulate into flat arrays and are scattered to the integrator once
 * at the end.
 *
 * The system must be charge-neutral (it is — every water molecule and
 * every ionic formula unit is neutral), so the net-charge correction is
 * zero and conducting ("tin-foil") boundaries are assumed.
 */
import { Vector3 } from 'three';
import { COULOMB_CONSTANT, GF_CONVERSION_CONSTANT } from '../physicalConstants.ts';
import { PeriodicBox } from './PeriodicBox.ts';

/** Complementary error function — Abramowitz & Stegun 7.1.26, x ≥ 0. */
const erfc = (x: number): number => {
  const t = 1 / (1 + 0.3275911 * x);
  const y = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  return y * Math.exp(-x * x);
};

/**
 * Grid resolution for the tabulated real-space Ewald kernel. The kernel
 * is smooth and the cutoff fixed, so a few-thousand-point table with
 * linear interpolation is accurate well past Ewald's own truncation
 * error — and it replaces a per-pair erfc + exp + sqrt with two lookups.
 */
const REAL_TABLE_POINTS = 4096;

/**
 * A charge-bearing point the Ewald sum operates on — a real atom or a
 * virtual site. `applyForce` routes an accumulated Cartesian force back
 * to the integrator (real atom → ÷mass; virtual site → split across its
 * parent atoms), so the Ewald code never has to know which it is.
 */
export interface ChargeEntity {
  position: Vector3;
  charge: number;
  moleculeName: string | undefined;
  dead: boolean;
  applyForce: (fx: number, fy: number, fz: number) => void;
}

export class EwaldSummation {
  private box!: PeriodicBox;
  private alpha = 0;
  private realCutoffSq = 0;
  private twoAlphaOverSqrtPi = 0;
  /** Reciprocal basis vectors b₀,b₁,b₂ — Cartesian components, one entry per axis. */
  private bx = [0, 0, 0];
  private by = [0, 0, 0];
  private bz = [0, 0, 0];
  /** Max integer index along each reciprocal axis (sets the table sizes). */
  private nMax = [0, 0, 0];
  /** Reciprocal vectors: integer indices, Cartesian components, energy coefficient. */
  private kn0: number[] = [];
  private kn1: number[] = [];
  private kn2: number[] = [];
  private kx: number[] = [];
  private ky: number[] = [];
  private kz: number[] = [];
  private kCoef: number[] = []; // C·(4π/V)·exp(−k²/4α²)/k² (the 4π folds in the ±k pair)
  private numK = 0;
  /**
   * Intramolecular index groups for the exclusion correction, cached
   * against the entity array. The kernel now reuses one charge-entity
   * array across steps, so molecule membership never changes — the
   * grouping (and its Map allocation) is built once, not every step.
   */
  private exclusionGroups: number[][] = [];
  private exclusionGroupsFor: ChargeEntity[] | null = null;
  /**
   * Multiple-time-step stride for the reciprocal-space sum: recompute it
   * only every Nth step and hold its force/energy constant in between.
   *
   * DEFAULT 1 (off). It used to be 3, and that was an energy pump, not a
   * shortcut — holding a force fixed while the atoms move is non-
   * conservative, and the error grows with how far they move between
   * refreshes. Measured 2026-10-06 on 2×2×2 TIP4P/Ice ice (192 atoms,
   * NVE, 1 fs): stride 3 heated the crystal from 200 K past 10 000 K in
   * 1.75 ps and went NaN; stride 1 held the total energy at −0.6428
   * eV/molecule to four decimals over 3 ps. Under a Berendsen thermostat
   * the pump was hidden as a crystal that sat at ~550 K with a 200 K
   * setpoint — i.e. every rigid-water run was quietly molten. Light,
   * fast, charged hydrogens make water the worst case; heavy ions drift
   * far less, but the bias is in the same direction (heating), so an
   * ionic melting sweep under stride 3 read low. And the saving was
   * nothing measurable: 3000 thermostatted steps took 8.1 s at either
   * setting — the reciprocal sum is cheap next to the real-space pass at
   * the cell sizes this lab runs.
   *
   * A stride > 1 would be legitimate only as textbook r-RESPA (the slow
   * force applied as half-kicks around an inner loop), which needs
   * integrator + SHAKE surgery. Until then, leave it at 1.
   */
  reciprocalStride = 1;
  /** Running step counter + cached reciprocal energy for the MTS. */
  private stepCounter = 0;
  private recipEnergy = 0;
  /**
   * Tabulated real-space kernel, indexed by r² (built in configure()).
   * realEnergyTab[k] = erfc(α·r)/r; realForceTab[k] is the force scalar
   * — see buildRealSpaceTables(). realTabInvDelta maps r² to a table
   * index. The pair loop interpolates these instead of calling erfc.
   */
  private realEnergyTab = new Float64Array(0);
  private realForceTab = new Float64Array(0);
  private realTabInvDelta = 0;

  // --- Scratch buffers, lazily (re)allocated to the entity count. ---
  private bufN = 0;
  private px = new Float64Array(0);
  private py = new Float64Array(0);
  private pz = new Float64Array(0);
  private qArr = new Float64Array(0);
  private fx = new Float64Array(0);
  private fy = new Float64Array(0);
  private fz = new Float64Array(0);
  /**
   * Cached reciprocal-space ("slow") force. Recomputed every
   * RECIPROCAL_STRIDE steps and held here in between; the per-step force
   * scattered to the integrator is the fast part (fx) + this.
   */
  private fxR = new Float64Array(0);
  private fyR = new Float64Array(0);
  private fzR = new Float64Array(0);
  private cosKR = new Float64Array(0);
  private sinKR = new Float64Array(0);
  /** exp(i·n·bₘ·r) tables, n = 0..nMax[m], laid out [entity·(nMax[m]+1) + n]. */
  private cosT: Float64Array[] = [new Float64Array(0), new Float64Array(0), new Float64Array(0)];
  private sinT: Float64Array[] = [new Float64Array(0), new Float64Array(0), new Float64Array(0)];

  /**
   * Pick the splitting parameter and build the reciprocal-vector list
   * for `box`. Called once when the periodic box is set.
   */
  configure(box: PeriodicBox): void {
    this.box = box;
    // Real-space cutoff: short and fixed (~9 Å), but never beyond the
    // minimum-image limit of a small box.
    const realCutoff = Math.min(9.0, 0.49 * box.minWidth);
    this.realCutoffSq = realCutoff * realCutoff;
    // Splitting parameter: erfc(α·rc) ≈ 2e-5 at α·rc = 3.
    this.alpha = 3.0 / realCutoff;
    this.twoAlphaOverSqrtPi = (2 * this.alpha) / Math.sqrt(Math.PI);
    this.buildRealSpaceTables();

    // Reciprocal lattice vectors = rows of 2π·H⁻¹. three.js Matrix3
    // stores elements column-major, so row i is (e[i], e[i+3], e[i+6]).
    const e = box.hInv.elements;
    const twoPi = 2 * Math.PI;
    const b0 = new Vector3(e[0], e[3], e[6]).multiplyScalar(twoPi);
    const b1 = new Vector3(e[1], e[4], e[7]).multiplyScalar(twoPi);
    const b2 = new Vector3(e[2], e[5], e[8]).multiplyScalar(twoPi);
    this.bx = [b0.x, b1.x, b2.x];
    this.by = [b0.y, b1.y, b2.y];
    this.bz = [b0.z, b1.z, b2.z];
    // Cell volume from the lattice (column) vectors of H.
    const h = box.h.elements;
    const a0 = new Vector3(h[0], h[1], h[2]);
    const a1 = new Vector3(h[3], h[4], h[5]);
    const a2 = new Vector3(h[6], h[7], h[8]);
    const volume = Math.abs(a0.dot(new Vector3().crossVectors(a1, a2)));

    // Reciprocal cutoff. exp(−k²/4α²) ≈ 1e-4 beyond k = 6α — ample, and
    // the total error is dominated by the real-space erfc cutoff
    // (~2e-5) anyway. 6α (vs the textbook 8α) is ~2.4× fewer k-vectors.
    const kCut = 6 * this.alpha;
    const kCutSq = kCut * kCut;
    const fourAlphaSqInv = 1 / (4 * this.alpha * this.alpha);
    const energyPrefactor = (COULOMB_CONSTANT * 4 * Math.PI) / volume;

    this.kn0 = [];
    this.kn1 = [];
    this.kn2 = [];
    this.kx = [];
    this.ky = [];
    this.kz = [];
    this.kCoef = [];
    const nMax0 = Math.ceil(kCut / b0.length());
    const nMax1 = Math.ceil(kCut / b1.length());
    const nMax2 = Math.ceil(kCut / b2.length());
    this.nMax = [nMax0, nMax1, nMax2];
    const k = new Vector3();
    for (let n0 = -nMax0; n0 <= nMax0; n0++) {
      for (let n1 = -nMax1; n1 <= nMax1; n1++) {
        for (let n2 = -nMax2; n2 <= nMax2; n2++) {
          // Half-space: keep exactly one of every ±k pair. n0 ends up
          // ≥ 0 always, so its table lookup never needs a conjugate.
          if (n0 < 0) continue;
          if (n0 === 0 && n1 < 0) continue;
          if (n0 === 0 && n1 === 0 && n2 <= 0) continue;
          k.set(0, 0, 0);
          k.addScaledVector(b0, n0);
          k.addScaledVector(b1, n1);
          k.addScaledVector(b2, n2);
          const kSq = k.lengthSq();
          if (kSq > kCutSq || kSq < 1e-12) continue;
          this.kn0.push(n0);
          this.kn1.push(n1);
          this.kn2.push(n2);
          this.kx.push(k.x);
          this.ky.push(k.y);
          this.kz.push(k.z);
          this.kCoef.push((energyPrefactor * Math.exp(-kSq * fourAlphaSqInv)) / kSq);
        }
      }
    }
    this.numK = this.kCoef.length;
  }

  /**
   * Precompute the real-space Ewald kernel onto a fine r² grid, so the
   * pair loop replaces a per-pair erfc + exp + sqrt with two
   * interpolated lookups. Rebuilt by configure() when the box (hence α)
   * changes. With r = √(k·Δ), Δ = realCutoffSq / REAL_TABLE_POINTS:
   *   realEnergyTab[k] = erfc(α·r)/r                  (pair energy qq·e)
   *   realForceTab[k]  = (erfc(α·r)/r² + (2α/√π)·e^{-α²r²}/r) / r
   *                                              (force scalar qq·f·GF)
   */
  private buildRealSpaceTables(): void {
    const n = REAL_TABLE_POINTS;
    const delta = this.realCutoffSq / n;
    this.realTabInvDelta = n / this.realCutoffSq;
    const eTab = new Float64Array(n + 1);
    const fTab = new Float64Array(n + 1);
    const alpha = this.alpha;
    const alphaSq = alpha * alpha;
    for (let k = 1; k <= n; k++) {
      const rsq = k * delta;
      const r = Math.sqrt(rsq);
      const erfcAr = erfc(alpha * r);
      const expAr = Math.exp(-alphaSq * rsq);
      eTab[k] = erfcAr / r;
      fTab[k] = (erfcAr / rsq + (this.twoAlphaOverSqrtPi * expAr) / r) / r;
    }
    // k = 0 is r = 0 (singular) and is never queried — the pair loop
    // skips rsq < 1e-12 and no real pair is that close — but give it a
    // finite value so an interpolation can't yield NaN.
    eTab[0] = eTab[1];
    fTab[0] = fTab[1];
    this.realEnergyTab = eTab;
    this.realForceTab = fTab;
  }

  /** (Re)allocate the scratch buffers for an entity count of `n`. */
  private ensureBuffers(n: number): void {
    if (n <= this.bufN) return;
    this.bufN = n;
    this.px = new Float64Array(n);
    this.py = new Float64Array(n);
    this.pz = new Float64Array(n);
    this.qArr = new Float64Array(n);
    this.fx = new Float64Array(n);
    this.fy = new Float64Array(n);
    this.fz = new Float64Array(n);
    this.fxR = new Float64Array(n);
    this.fyR = new Float64Array(n);
    this.fzR = new Float64Array(n);
    this.cosKR = new Float64Array(n);
    this.sinKR = new Float64Array(n);
    for (let m = 0; m < 3; m++) {
      this.cosT[m] = new Float64Array(n * (this.nMax[m] + 1));
      this.sinT[m] = new Float64Array(n * (this.nMax[m] + 1));
    }
  }

  /**
   * Total electrostatic energy of the charge set, with forces applied
   * through each entity's `applyForce`. Forces are pre-multiplied by
   * GF_CONVERSION_CONSTANT (the engine's acceleration convention).
   */
  compute(entities: ChargeEntity[]): number {
    const n = entities.length;
    if (n === 0) return 0;
    this.ensureBuffers(n);
    const { px, py, pz, qArr, fx, fy, fz, fxR, fyR, fzR } = this;
    for (let i = 0; i < n; i++) {
      const e = entities[i];
      px[i] = e.position.x;
      py[i] = e.position.y;
      pz[i] = e.position.z;
      qArr[i] = e.charge;
      fx[i] = 0;
      fy[i] = 0;
      fz[i] = 0;
    }

    let energy = 0;
    // Fast (short-range) parts — recomputed every step into fx/fy/fz.
    energy += this.realSpace(entities, n);
    energy += this.selfEnergy(n);
    energy += this.exclusionCorrection(entities, n);
    // Slow (reciprocal-space) part — the costly sum. Multiple-time-step:
    // refresh it only every `reciprocalStride` steps; on the steps in
    // between, reuse the cached force (fxR/fyR/fzR) and energy. The
    // reciprocal force is smooth and slowly varying, so holding it for a
    // step or two is a small, well-behaved approximation that skips most
    // of the Ewald cost.
    if (this.stepCounter % this.reciprocalStride === 0) {
      this.recipEnergy = this.reciprocalSpace(n);
    }
    this.stepCounter++;
    energy += this.recipEnergy;

    // Scatter fast + cached-slow force (already ×GF) to the integrator.
    for (let i = 0; i < n; i++) {
      entities[i].applyForce(fx[i] + fxR[i], fy[i] + fyR[i], fz[i] + fzR[i]);
    }
    return energy;
  }

  /**
   * Real-space sum: C·Σ qᵢqⱼ·erfc(αr)/r within the cutoff (skip
   * intramolecular). The erfc kernel is read from the precomputed
   * r²-indexed table (buildRealSpaceTables) — no per-pair erfc/exp/sqrt.
   */
  private realSpace(entities: ChargeEntity[], n: number): number {
    let energy = 0;
    const { px, py, pz, qArr, fx, fy, fz, realEnergyTab, realForceTab } = this;
    const GF = GF_CONVERSION_CONSTANT;
    const realCutoffSq = this.realCutoffSq;
    const invDelta = this.realTabInvDelta;
    const v = new Vector3();
    for (let i = 0; i < n - 1; i++) {
      const ei = entities[i];
      for (let j = i + 1; j < n; j++) {
        const ej = entities[j];
        if (ei.dead && ej.dead) continue;
        if (ei.moleculeName !== undefined && ei.moleculeName === ej.moleculeName) continue;
        v.set(px[i] - px[j], py[i] - py[j], pz[i] - pz[j]);
        this.box.minimumImage(v);
        const rsq = v.lengthSq();
        if (rsq >= realCutoffSq || rsq < 1e-12) continue;
        const qq = COULOMB_CONSTANT * qArr[i] * qArr[j];
        // Interpolate the tabulated erfc kernel at this r².
        const tIdx = rsq * invDelta;
        const k = tIdx | 0;
        const frac = tIdx - k;
        const eFac = realEnergyTab[k] + (realEnergyTab[k + 1] - realEnergyTab[k]) * frac;
        const fFac = realForceTab[k] + (realForceTab[k + 1] - realForceTab[k]) * frac;
        energy += qq * eFac;
        const fij = qq * fFac * GF;
        fx[i] += fij * v.x;
        fy[i] += fij * v.y;
        fz[i] += fij * v.z;
        fx[j] -= fij * v.x;
        fy[j] -= fij * v.y;
        fz[j] -= fij * v.z;
      }
    }
    return energy;
  }

  /** Reciprocal-space sum with incremental structure factors. */
  private reciprocalSpace(n: number): number {
    const { px, py, pz, qArr, fxR, fyR, fzR, cosKR, sinKR, cosT, sinT, nMax, bx, by, bz } = this;
    // This force REPLACES the cached slow force — zero it first.
    for (let i = 0; i < n; i++) {
      fxR[i] = 0;
      fyR[i] = 0;
      fzR[i] = 0;
    }

    // Build exp(i·n·bₘ·r) tables: one cos/sin per (entity, axis), the
    // rest by recurrence cos(nθ) = cos((n−1)θ)cosθ − sin((n−1)θ)sinθ.
    for (let m = 0; m < 3; m++) {
      const stride = nMax[m] + 1;
      const ct = cosT[m];
      const st = sinT[m];
      const bmx = bx[m];
      const bmy = by[m];
      const bmz = bz[m];
      for (let i = 0; i < n; i++) {
        const base = i * stride;
        const theta = bmx * px[i] + bmy * py[i] + bmz * pz[i];
        const c1 = Math.cos(theta);
        const s1 = Math.sin(theta);
        ct[base] = 1;
        st[base] = 0;
        if (stride > 1) {
          ct[base + 1] = c1;
          st[base + 1] = s1;
          for (let nn = 2; nn < stride; nn++) {
            ct[base + nn] = ct[base + nn - 1] * c1 - st[base + nn - 1] * s1;
            st[base + nn] = st[base + nn - 1] * c1 + ct[base + nn - 1] * s1;
          }
        }
      }
    }

    const GF = GF_CONVERSION_CONSTANT;
    const stride0 = nMax[0] + 1;
    const stride1 = nMax[1] + 1;
    const stride2 = nMax[2] + 1;
    const ct0 = cosT[0];
    const st0 = sinT[0];
    const ct1 = cosT[1];
    const st1 = sinT[1];
    const ct2 = cosT[2];
    const st2 = sinT[2];
    let energy = 0;

    for (let kIdx = 0; kIdx < this.numK; kIdx++) {
      const n0 = this.kn0[kIdx]; // ≥ 0
      const n1 = this.kn1[kIdx];
      const n2 = this.kn2[kIdx];
      const a1 = n1 < 0 ? -n1 : n1;
      const a2 = n2 < 0 ? -n2 : n2;
      const sign1 = n1 < 0 ? -1 : 1; // exp(i·(−n)·θ) = conjugate of exp(i·n·θ)
      const sign2 = n2 < 0 ? -1 : 1;
      let sRe = 0;
      let sIm = 0;
      for (let i = 0; i < n; i++) {
        // exp(i·k·rᵢ) = exp(i·n0·θ0)·exp(i·n1·θ1)·exp(i·n2·θ2).
        const c0 = ct0[i * stride0 + n0];
        const s0 = st0[i * stride0 + n0];
        const c1 = ct1[i * stride1 + a1];
        const s1 = st1[i * stride1 + a1] * sign1;
        const c2 = ct2[i * stride2 + a2];
        const s2 = st2[i * stride2 + a2] * sign2;
        const e01Re = c0 * c1 - s0 * s1;
        const e01Im = c0 * s1 + s0 * c1;
        const cosKr = e01Re * c2 - e01Im * s2;
        const sinKr = e01Re * s2 + e01Im * c2;
        cosKR[i] = cosKr;
        sinKR[i] = sinKr;
        sRe += qArr[i] * cosKr;
        sIm += qArr[i] * sinKr;
      }
      const coef = this.kCoef[kIdx];
      energy += coef * (sRe * sRe + sIm * sIm);
      const forcePre = 2 * coef * GF;
      const kxv = this.kx[kIdx];
      const kyv = this.ky[kIdx];
      const kzv = this.kz[kIdx];
      for (let i = 0; i < n; i++) {
        const s = forcePre * qArr[i] * (sRe * sinKR[i] - sIm * cosKR[i]);
        fxR[i] += s * kxv;
        fyR[i] += s * kyv;
        fzR[i] += s * kzv;
      }
    }
    return energy;
  }

  /** Self-energy −C·(α/√π)·Σ qᵢ². Position-independent → no force. */
  private selfEnergy(n: number): number {
    let sumQsq = 0;
    for (let i = 0; i < n; i++) sumQsq += this.qArr[i] * this.qArr[i];
    return -((COULOMB_CONSTANT * this.alpha) / Math.sqrt(Math.PI)) * sumQsq;
  }

  /**
   * Exclusion correction: the reciprocal sum implicitly counted every
   * intramolecular pair; subtract C·qᵢqⱼ·erf(αr)/r for each, since a
   * rigid molecule must have no explicit Coulomb between its own sites.
   */
  private exclusionCorrection(entities: ChargeEntity[], n: number): number {
    let energy = 0;
    const { px, py, pz, qArr, fx, fy, fz } = this;
    const alpha = this.alpha;
    const alphaSq = alpha * alpha;
    const GF = GF_CONVERSION_CONSTANT;
    // Group intramolecular indices once. The entity array is reused
    // across steps, so molecule membership is stable — rebuild only if a
    // different array comes in (a new engine / supercell).
    if (this.exclusionGroupsFor !== entities) {
      const groups = new Map<string, number[]>();
      for (let i = 0; i < n; i++) {
        const m = entities[i].moleculeName;
        if (m === undefined) continue;
        let g = groups.get(m);
        if (!g) {
          g = [];
          groups.set(m, g);
        }
        g.push(i);
      }
      this.exclusionGroups = [...groups.values()];
      this.exclusionGroupsFor = entities;
    }
    const v = new Vector3();
    for (const idxs of this.exclusionGroups) {
      for (let a = 0; a < idxs.length - 1; a++) {
        for (let bIdx = a + 1; bIdx < idxs.length; bIdx++) {
          const i = idxs[a];
          const j = idxs[bIdx];
          v.set(px[i] - px[j], py[i] - py[j], pz[i] - pz[j]);
          this.box.minimumImage(v);
          const rsq = v.lengthSq();
          if (rsq < 1e-12) continue;
          const r = Math.sqrt(rsq);
          const qq = COULOMB_CONSTANT * qArr[i] * qArr[j];
          const erfAr = 1 - erfc(alpha * r);
          const expAr = Math.exp(-alphaSq * rsq);
          energy -= (qq * erfAr) / r;
          // Force from −qq·erf(αr)/r: qq·[(2α/√π)·e^{−α²r²}/r − erf(αr)/r²].
          const forceRadial = qq * ((this.twoAlphaOverSqrtPi * expAr) / r - erfAr / rsq);
          const fij = (forceRadial / r) * GF;
          fx[i] += fij * v.x;
          fy[i] += fij * v.y;
          fz[i] += fij * v.z;
          fx[j] -= fij * v.x;
          fy[j] -= fij * v.y;
          fz[j] -= fij * v.z;
        }
      }
    }
    return energy;
  }
}
